/* --------------------------------------------------------------------------
   Talking to the SchedPilot Chrome extension.

   The extension lists app.schedpilot.com (and localhost) under
   externally_connectable, so this page can message it directly with
   chrome.runtime.sendMessage(id, ...). When the extension is not installed —
   or is an older version without that — window.chrome.runtime is missing or
   the message fails, and every helper here resolves to null.

   Every known extension ID is tried: the Chrome Web Store build, the unpacked
   build, and REACT_APP_EXTENSION_ID if set (see .env.example). The first one
   that answers PING is remembered and used for every later message.
   -------------------------------------------------------------------------- */

export const EXTENSION_IDS = [
  process.env.REACT_APP_EXTENSION_ID,
  'iphdmkpjdmpccppgdejahkidlnekmnad',   // Chrome Web Store
  'oafcfijgocpbdiochbgicdnikjemcedd',   // unpacked build
].filter((id, i, all) => id && all.indexOf(id) === i);

export const EXTENSION_STORE_URL = 'https://chromewebstore.google.com/detail/schedpilot/iphdmkpjdmpccppgdejahkidlnekmnad';

let activeId = null;   // the ID that answered, once detectExtension() has found one

/** Send one message to one extension ID. Resolves to its reply, or null if it is not there. */
function sendTo(id, message, timeoutMs) {
  return new Promise((resolve) => {
    const runtime = window.chrome && window.chrome.runtime;
    if (!runtime || typeof runtime.sendMessage !== 'function') return resolve(null);

    let done = false;
    const finish = (value) => { if (!done) { done = true; resolve(value); } };
    const timer = setTimeout(() => finish(null), timeoutMs);
    try {
      runtime.sendMessage(id, message, (reply) => {
        clearTimeout(timer);
        // Reading lastError marks it handled, so Chrome does not log it.
        finish(runtime.lastError ? null : reply || null);
      });
    } catch (e) {
      clearTimeout(timer);
      finish(null);
    }
  });
}

/** { ok, version, loggedIn, id } from the first installed extension (v1.0.9+), otherwise null. */
export async function detectExtension() {
  const replies = await Promise.all(EXTENSION_IDS.map((id) => sendTo(id, { type: 'PING' }, 2500)));
  const i = replies.findIndex((r) => r && r.ok);
  activeId = i === -1 ? null : EXTENSION_IDS[i];
  return i === -1 ? null : { ...replies[i], id: activeId };
}

/** Send one message to the installed extension. Resolves to its reply, or null if none is there. */
export async function sendToExtension(message, timeoutMs = 15000) {
  if (!activeId && !(await detectExtension())) return null;
  return sendTo(activeId, message, timeoutMs);
}

/** Hand this app's SchedPilot login to the extension, so the user does not log in twice. */
export async function connectExtension() {
  const token = localStorage.getItem('jwtToken');
  if (!token) return { ok: false, reason: 'NOT_LOGGED_IN_APP' };
  return (await sendToExtension({ type: 'SCHEDPILOT_AUTH', token })) || { ok: false, reason: 'NO_EXTENSION' };
}
